import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { updateSEOMeta, injectJSONLD } from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { articles, featuredArticleSlugs, articleMap } from '@/mocks/articles';
import { faqs } from '@/mocks/faq';

export default function Home() {
  useEffect(() => {
    updateSEOMeta({
      title: '抖ying官方入口 · 抖ying短视频平台完整指南 — 注册、运营、涨粉、变现一站式攻略',
      description: '抖ying是当前最热门的短视频社交平台，本页为你详解抖ying的注册登录、内容创作、流量变现及算法运营技巧，帮助创作者和品牌主快速掌握抖ying核心玩法，实现粉丝增长与商业变现。',
      keywords: '抖ying,抖ying运营技巧,抖ying涨粉方法,抖ying变现攻略,抖ying算法解析,抖ying直播带货,抖ying企业号',
      canonicalUrl: 'https://douyin-guide.com/',
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '抖ying指南',
      url: 'https://douyin-guide.com',
      description: '抖ying短视频平台完整指南',
      potentialAction: {
        '@type': 'SearchAction',
        'target': 'https://douyin-guide.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    }, 'schema-website');

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer.replace(/<[^>]*>/g, ''),
        },
      })),
    }, 'schema-faq');
  }, []);

  const featuredArticles = featuredArticleSlugs
    .map((id) => articleMap[id])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20abstract%20geometric%20composition%20with%20vibrant%20warm%20orange%20coral%20and%20soft%20cream%20gradients%2C%20dynamic%20flowing%20shapes%20representing%20digital%20creativity%20and%20social%20media%20energy%2C%20minimalist%20design%20with%20smooth%20curves%20and%20layered%20depth%2C%20clean%20contemporary%20aesthetic%20with%20soft%20lighting%20and%20ethereal%20atmosphere&width=1800&height=900&seq=hero-douyin-2025&orientation=landscape"
            alt="抖ying短视频平台完整指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4 md:mb-6">
              抖ying短视频平台
              <br />
              <span className="text-primary-400">完整运营指南</span>
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              从注册入门到商业变现，全面掌握抖ying运营技巧、算法机制和涨粉方法
              ——帮助每一位创作者和品牌主在抖ying实现增长突破
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rumen"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-rocket-line"></i>
                新手入门指南
              </Link>
              <Link
                to="/category/yunying"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full text-base font-medium hover:bg-background-50/25 transition-all whitespace-nowrap"
              >
                <i className="ri-line-chart-line"></i>
                运营进阶技巧
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="relative -mt-10 z-20 max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 rounded-2xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { value: '77+', label: '篇深度文章', icon: 'ri-article-line' },
              { value: '6', label: '大内容分类', icon: 'ri-folder-line' },
              { value: '50万+', label: '累计阅读量', icon: 'ri-eye-line' },
              { value: '98%', label: '读者好评率', icon: 'ri-heart-line' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${stat.icon} text-lg text-primary-600`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{stat.value}</div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Category Section */}
        <section className="pt-16 md:pt-20 pb-12 md:pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                探索内容分类
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                六大分类覆盖抖ying运营全链路，从入门到精通，找到你需要的每一篇干货
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 no-underline"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-2">
                        {cat.description}
                      </p>
                      <span className="text-xs text-foreground-400">
                        {cat.articleCount} 篇文章
                      </span>
                    </div>
                    <div className="flex-shrink-0 flex items-center pt-1">
                      <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  精选热门文章
                </h2>
                <p className="text-foreground-600 text-sm md:text-base">
                  涵盖抖ying运营最核心的知识领域，由资深创作者倾心撰写
                </p>
              </div>
              <Link
                to="/category/yunying"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-xs font-medium text-primary-600 rounded-full">
                        {categories.find((c) => c.slug === article.category)?.name || article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400 pt-3 border-t border-background-100">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                关于抖ying运营的常见疑问，这里为你一一解答
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <h4 className="text-sm md:text-base font-semibold text-foreground-900 pr-6">
                      {faq.question}
                    </h4>
                    <div className="faq-icon w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-colors">
                      <i className="ri-add-line text-sm transition-transform duration-200"></i>
                    </div>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20gradient%20with%20warm%20coral%20orange%20and%20cream%20tones%2C%20soft%20organic%20curves%2C%20minimalist%20geometric%20composition%2C%20clean%20modern%20design%2C%20subtle%20depth%20with%20layered%20translucent%20shapes%2C%20warm%20and%20inviting%20atmosphere&width=1800&height=600&seq=cta-douyin-footer&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-primary-500/90"></div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-4">
              准备好开启你的抖ying之旅了吗？
            </h2>
            <p className="text-background-50/80 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
              从今天开始，系统学习抖ying运营知识，掌握涨粉变现的核心方法，让你的内容被更多人看到
            </p>
            <Link
              to="/category/rumen"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-50/90 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-play-circle-line"></i>
              立即开始学习
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}